"use client";

import React, { useState, useRef } from "react";
import {
  Upload,
  FileVideo,
  Sparkles,
  CheckCircle,
  Download,
  AlertCircle,
  RefreshCw,
  X,
} from "lucide-react";

const UploadSection = () => {
  const [isDragOver, setIsDragOver] = useState<boolean>(false);
  const [uploadedFile, setUploadedFile] = useState<any>(null);
  const [isConverting, setIsConverting] = useState<boolean>(false);
  const [isConverted, setIsConverted] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showErrorPopup, setShowErrorPopup] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const fileInputRef = useRef<any>(null);
  const [audioUrl, setAudioUrl] = useState(null);

  const handleDragOver = (e: any) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: any) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: any) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const config = {
    api: {
      bodyParser: {
        sizeLimit: "1024mb", // Increase to desired limit
      },
    },
  };

  const handleFileUpload = (file: any) => {
    const validVideoTypes = ["video/quicktime", "video/mp4"];
    const validExtensions = [".mov", ".mp4"];

    const fileName = file.name?.toLowerCase() || "";
    const fileExt = fileName.substring(fileName.lastIndexOf("."));

    const isValidMime = validVideoTypes.includes(file.type) || file.type.startsWith("video/");
    const isValidExt = validExtensions.includes(fileExt);

    if (file && (isValidMime || isValidExt)) {
      setUploadedFile(file);
      setIsConverted(false);
      setError(null);
      setShowErrorPopup(false);
    } else {
      alert("Invalid file type. Please upload a valid video file (.MOV or .MP4).");
    }
  };

  const handleFileInputChange = (e: any) => {
    if (e.target.files.length > 0) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const handleConvert = async (format: any) => {
    setIsConverting(true);
    setIsUploading(true);
    setIsProcessing(false);
    setUploadProgress(0);
    setError(null);
    setShowErrorPopup(false);

    try {
      const formData = new FormData();
      formData.append("file", uploadedFile);

      const xhr = new XMLHttpRequest();

      // Track upload progress
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(percentComplete);

          if (percentComplete === 100) {
            setIsUploading(false);
            setIsProcessing(true);
          }
        }
      };

      // Handle the response
      const response = await new Promise<Response>((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            // Create a Response object from the XMLHttpRequest
            const response = new Response(xhr.responseText, {
              status: xhr.status,
              statusText: xhr.statusText,
              headers: new Headers({
                'Content-Type': xhr.getResponseHeader('Content-Type') || 'application/json'
              })
            });
            resolve(response);
          } else {
            reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
          }
        };

        xhr.onerror = () => reject(new Error('Network error occurred'));
        xhr.ontimeout = () => reject(new Error('Request timed out'));

        xhr.open('POST', process.env.NEXT_PUBLIC_UPLOAD_BACKEND_API_URL + "tools/please/mp4");
        xhr.send(formData);
      });

      if (!response.ok) {
        let errorMessage = "Conversion failed. Please try again.";

        switch (response.status) {
          case 422:
            errorMessage = "Invalid file format or corrupted file. Please check your video file.";
            break;
          case 500:
            errorMessage = "Server error occurred. Please try again later.";
            break;
          case 413:
            errorMessage = "File too large. Please upload a smaller video file.";
            break;
          case 429:
            errorMessage = "Too many requests. Please wait a moment and try again.";
            break;
          default:
            errorMessage = `Error ${response.status}: ${response.statusText || "Unknown error occurred"}`;
        }

        throw new Error(errorMessage);
      }

      const data = await response.json();

      if (!data.output) {
        throw new Error("No output received from server. Please try again.");
      }

      setAudioUrl(data.output ?? null);
      setIsConverting(false);
      setIsUploading(false);
      setIsProcessing(false);
      setIsConverted(true);
      setUploadProgress(0);
    } catch (error: any) {
      console.error("API Error:", error);
      setIsConverting(false);
      setIsUploading(false);
      setIsProcessing(false);
      setUploadProgress(0);
      setError(error.message || "An unexpected error occurred. Please try again.");
      setShowErrorPopup(true);
    }
  };

  const handleRetry = () => {
    setError(null);
    setShowErrorPopup(false);
    handleConvert("mp4");
  };

  const closeErrorPopup = () => {
    setShowErrorPopup(false);
  };

  const formatFileSize = (bytes: any) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
      <div className="max-w-4xl mx-auto relative">
        {/* Error Popup */}
        {showErrorPopup && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative">
                <button
                    onClick={closeErrorPopup}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>

                <div className="text-center">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertCircle className="w-8 h-8 text-red-500" />
                  </div>

                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    Conversion Failed
                  </h3>

                  <p className="text-gray-600 mb-6">
                    {error}
                  </p>

                  <div className="flex gap-3">
                    <button
                        onClick={closeErrorPopup}
                        className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                        onClick={handleRetry}
                        className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-4 py-2 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Retry
                    </button>
                  </div>
                </div>
              </div>
            </div>
        )}

        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/50 p-4">
          {!uploadedFile ? (
              <div
                  className={`border-3 border-dashed rounded-2xl p-12 text-center transition-all duration-300 cursor-pointer ${
                      isDragOver
                          ? "border-purple-500 bg-purple-50 scale-105"
                          : "border-gray-300 hover:border-purple-400 hover:bg-gray-50"
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
              >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".mov"
                    onChange={handleFileInputChange}
                    className="hidden"
                />

                <div className="">
                  <div className="mx-auto w-18 h-18 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full flex items-center justify-center mb-4 shadow-lg">
                    <Upload className="w-10 h-10 text-white" />
                  </div>
                </div>

                <h3 className="text-2xl font-semibold text-gray-800">
                  Drop your video file here
                </h3>
                <p className="text-gray-600 mb-3">
                  or click to browse from your device
                </p>

                <div className="flex flex-wrap justify-center gap-3 mb-6">
                  {[".mov", ".mp4"].map((format) => (
                      <span
                          key={format}
                          className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium"
                      >
                  {format}
                </span>
                  ))}
                </div>

                <button className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg">
                  Choose Video File
                </button>
                <p className="mt-5 text-sm">Files must not exceed 1 GB in size</p>
              </div>
          ) : (
              <div className="space-y-6">
                {/* File Info */}
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-2xl p-6 border border-purple-200">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-xl flex items-center justify-center">
                        <FileVideo className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800">
                          {uploadedFile.name}
                        </h4>
                        <p className="text-sm text-gray-600">
                          {formatFileSize(uploadedFile.size)}
                        </p>
                      </div>
                    </div>
                    <button
                        onClick={() => {
                          setUploadedFile(null);
                          setIsConverted(false);
                          setIsConverting(false);
                          setIsUploading(false);
                          setIsProcessing(false);
                          setUploadProgress(0);
                          setError(null);
                          setShowErrorPopup(false);
                        }}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      ✕
                    </button>
                  </div>

                  {/* Show error state inline */}
                  {error && !showErrorPopup && (
                      <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl">
                        <div className="flex items-center gap-3 text-red-700 mb-3">
                          <AlertCircle className="w-5 h-5" />
                          <span className="font-semibold">Conversion Failed</span>
                        </div>
                        <p className="text-red-600 text-sm mb-3">{error}</p>
                        <button
                            onClick={handleRetry}
                            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2 text-sm"
                        >
                          <RefreshCw className="w-4 h-4" />
                          Retry Upload
                        </button>
                      </div>
                  )}

                  {!isConverted && !isConverting && !error ? (
                      <a
                          onClick={() => handleConvert("mp4")}
                          className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white py-4 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center justify-center gap-2 cursor-pointer"
                      >
                        <Sparkles className="w-5 h-5" />
                        Free .mp4
                      </a>
                  ) : (
                      audioUrl && (
                          <div className="w-full flex justify-center">
                            <video
                                controls
                                src={audioUrl}
                                className="rounded-xl shadow-lg max-w-full h-[300px]"
                            />
                          </div>
                      )
                  )}

                  {isConverting && (
                      <div className="text-center py-8">
                        {isUploading ? (
                            <div>
                              <div className="inline-flex items-center gap-3 text-purple-600 mb-4">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                                <span className="font-semibold">
                          Uploading... {uploadProgress}%
                        </span>
                              </div>
                              <div className="bg-gray-200 rounded-full h-3 overflow-hidden mb-2">
                                <div
                                    className="bg-gradient-to-r from-purple-600 to-indigo-600 h-full rounded-full transition-all duration-300 ease-out"
                                    style={{ width: `${uploadProgress}%` }}
                                ></div>
                              </div>
                              <p className="text-sm text-gray-600">
                                {uploadProgress < 100 ? "Please wait while we upload your file..." : "Upload complete, starting conversion..."}
                              </p>
                            </div>
                        ) : isProcessing ? (
                            <div>
                              <div className="inline-flex items-center gap-3 text-purple-600 mb-4">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                                <span className="font-semibold">
                          Processing your video...
                        </span>
                              </div>
                              <div className="bg-gray-200 rounded-full h-3 overflow-hidden mb-2">
                                <div className="bg-gradient-to-r from-purple-600 to-indigo-600 h-full rounded-full animate-pulse w-full"></div>
                              </div>
                              <p className="text-sm text-gray-600">
                                Converting your video to MP4, this may take a few moments...
                              </p>
                            </div>
                        ) : (
                            <div>
                              <div className="inline-flex items-center gap-3 text-purple-600">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                                <span className="font-semibold">
                          Initializing...
                        </span>
                              </div>
                            </div>
                        )}
                      </div>
                  )}

                  {isConverted && audioUrl && (
                      <div className="text-center py-6">
                        <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                          <CheckCircle className="w-8 h-8 text-white" />
                        </div>
                        <h4 className="text-xl font-semibold text-gray-800 mb-2">
                          Conversion Complete!
                        </h4>
                        <p className="text-gray-600 mb-6">
                          Your .mp4 file is ready for download
                        </p>
                        <a
                            href={audioUrl}
                            download
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center justify-center gap-2 mx-auto w-fit"
                        >
                          <Download className="w-5 h-5" />
                          Download .mp4
                        </a>
                      </div>
                  )}
                </div>
              </div>
          )}
        </div>

        {/* Trust indicators */}
        <div className="text-center mt-12">
          <p className="text-sm text-gray-500 mb-4">
            Trusted by content creators worldwide
          </p>
          <div className="flex justify-center items-center gap-8 opacity-60">
            <div className="text-2xl font-bold text-gray-400">
              100+ Files Converted
            </div>
            <div className="text-2xl font-bold text-gray-400">•</div>
            <div className="text-2xl font-bold text-gray-400">100% Free</div>
          </div>
        </div>
      </div>
  );
};

export default UploadSection;